import {Stack, Typography, useTheme} from "@mui/joy"
import {Link} from "react-router-dom"
import {Lock, Sparkles} from "lucide-react"
import {useTranslation} from "react-i18next"
import Button from "./Button"
import {AnalyzeDenial} from "../context/AnalyzeContext"
import {activePalette, applyTransparency} from "../theme"

/** Each refusal says something different, and sends the visitor to the same place */
const DENIAL_KEYS: { [reason in AnalyzeDenial["reason"]]: { title: string, description: string } } = {
    no_subscription: {title: "denialNoSubscriptionTitle", description: "denialNoSubscriptionDescription"},
    subscription_expired: {title: "denialExpiredTitle", description: "denialExpiredDescription"},
    limit_reached: {title: "denialLimitTitle", description: "denialLimitDescription"}
}

const formatTime = (value?: number) => value
    ? new Date(value).toLocaleTimeString(undefined, {hour: "2-digit", minute: "2-digit"})
    : ""

/**
 * What the analysis page shows instead of predictions when the plan does not cover them.
 *
 * The API refuses these on its own, so the notice only reads its answer back: what ran out, and the
 * one thing that fixes it.
 */
const SubscriptionNotice = ({denial}: { denial: AnalyzeDenial }) => {
    const {t} = useTranslation()
    const palette = activePalette(useTheme())
    const copy = DENIAL_KEYS[denial.reason] || DENIAL_KEYS.no_subscription

    return <Stack gap={2} alignItems={"center"} textAlign={"center"}
                  sx={{
                      padding: 3,
                      borderRadius: "15px",
                      background: applyTransparency(palette.primary["500"], 0.12),
                      border: `1px solid ${applyTransparency(palette.primary["600"], 0.35)}`
                  }}>
        <Lock size={28} style={{color: palette.primary["700"]}}/>

        <Stack gap={0.5}>
            <Typography level={"title-lg"}>{t(copy.title)}</Typography>
            <Typography level={"body-sm"}>{t(copy.description)}</Typography>
            {denial.reason === "limit_reached" && !!denial.resetAt &&
                <Typography level={"body-xs"}>
                    {t("denialResetAt", {time: formatTime(denial.resetAt)})}
                </Typography>}
        </Stack>

        <Link to={"/pricing"} style={{textDecoration: "none"}}>
            <Button startDecorator={<Sparkles size={18}/>}>{t("seePlans")}</Button>
        </Link>
    </Stack>
}

export default SubscriptionNotice
